import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Film, Image, Video, FileText, Download, FolderOpen, 
  ChevronRight, Search, Grid, List, Check, X, LogOut,
  Package, Loader2, File
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';
const R2_PUBLIC_URL = 'https://pub-550dbebe210d4a2e9beed373b33a1002.r2.dev';

// Get file icon based on type
const getFileIcon = (type) => {
  if (type?.startsWith('video/')) return Video;
  if (type?.startsWith('image/')) return Image;
  if (type === 'application/pdf') return FileText;
  return File;
};

// Format file size
const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function MediaPortalPage() {
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [selectedShow, setSelectedShow] = useState(null);
  const [assets, setAssets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Check auth
  useEffect(() => {
    const mediaAuth = sessionStorage.getItem('mediaAuth');
    if (mediaAuth !== 'true') {
      navigate('/media-login');
    }
  }, [navigate]);

  // Load shows
  useEffect(() => {
    const fetchShows = async () => {
      try {
        const response = await fetch(`${API_URL}/api/media/shows`);
        if (response.ok) {
          const data = await response.json();
          setShows(data);
        }
      } catch (err) {
        console.error('Error loading shows:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchShows();
  }, []);

  // Load assets when show selected
  useEffect(() => {
    if (!selectedShow) {
      setAssets([]);
      return;
    }
    
    const fetchAssets = async () => {
      setLoadingAssets(true);
      try {
        const response = await fetch(`${API_URL}/api/media/assets/${selectedShow.id}`);
        if (response.ok) {
          const data = await response.json();
          setAssets(data.assets || []);
        }
      } catch (err) {
        console.error('Error loading assets:', err);
      } finally {
        setLoadingAssets(false);
      }
    };
    fetchAssets();
  }, [selectedShow]);

  const handleLogout = () => {
    sessionStorage.removeItem('mediaAuth');
    navigate('/media-login');
  };

  const handleSelectAsset = (asset) => {
    setSelectedAssets(prev => {
      const exists = prev.find(a => a.name === asset.name);
      if (exists) {
        return prev.filter(a => a.name !== asset.name);
      }
      return [...prev, asset];
    });
  };

  const handleSelectAll = () => {
    const allSelected = assets.every(a => selectedAssets.find(s => s.name === a.name));
    if (allSelected) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets([...assets]);
    }
  };

  const handleDownloadSingle = (asset) => {
    const link = document.createElement('a');
    link.href = asset.url;
    link.download = asset.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadSelected = async () => {
    setDownloading(true);
    // Download each file
    for (const asset of selectedAssets) {
      handleDownloadSingle(asset);
      await new Promise(resolve => setTimeout(resolve, 500)); // Stagger downloads
    }
    setDownloading(false);
    setSelectedAssets([]);
  };

  const filteredShows = shows.filter(show => 
    show.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    show.shortTitle?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAssets = assets.filter(asset =>
    asset.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const CATEGORY_ORDER = ['Gallery', 'Promo', 'AdditionalVideos', 'SocialVideos', 'Posters', 'Banners', 'Documents', 'Other'];
  const CATEGORY_LABELS = {
    Gallery: 'Gallery', Promo: 'Promo Video', AdditionalVideos: 'Additional Videos',
    SocialVideos: 'Social Videos', Posters: 'Posters', Banners: 'Banners',
    Documents: 'Documents', Other: 'Other Files'
  };
  const groupedAssets = CATEGORY_ORDER
    .map(cat => ({ cat, items: filteredAssets.filter(a => (a.category || 'Other') === cat) }))
    .filter(g => g.items.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020120] via-[#08065E] to-[#020120]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#020120]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-xl flex items-center justify-center">
                <Film className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Media Portal</h1>
                <p className="text-white/50 text-sm">Mark Thompson Productions</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {selectedAssets.length > 0 && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={handleDownloadSelected}
                  disabled={downloading}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#020120] rounded-lg font-semibold hover:bg-white transition-colors disabled:opacity-50"
                >
                  {downloading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  Download ({selectedAssets.length})
                </motion.button>
              )}
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm mb-8">
          <button 
            onClick={() => { setSelectedShow(null); setSelectedAssets([]); setSearchQuery(''); }}
            className={`transition-colors ${!selectedShow ? 'text-[#D4AF37]' : 'text-white/50 hover:text-white'}`}
          >
            All Shows
          </button>
          {selectedShow && (
            <>
              <ChevronRight className="w-4 h-4 text-white/30" />
              <span className="text-[#D4AF37]">{selectedShow.shortTitle}</span>
            </>
          )}
        </div>

        <AnimatePresence mode="wait">
          {/* Shows Grid */}
          {!selectedShow && (
            <motion.div
              key="shows"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Search */}
              <div className="relative max-w-md mb-8">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search shows..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50"
                />
              </div>

              {/* Shows Grid */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {filteredShows.map((show) => (
                    <motion.button
                      key={show.id}
                      onClick={() => setSelectedShow(show)}
                      className="group text-left bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-[#D4AF37]/50 hover:bg-white/10 transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="aspect-[3/4] bg-white/5 relative overflow-hidden">
                        {show.poster && (
                          <img 
                            src={`${process.env.PUBLIC_URL}${show.poster}`}
                            alt={show.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <p className="text-white font-bold text-lg leading-tight">{show.shortTitle || show.title}</p>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Assets View */}
          {selectedShow && (
            <motion.div
              key="assets"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Show Header */}
              <div className="flex items-center gap-6 mb-8 pb-8 border-b border-white/10">
                <div className="w-24 h-32 bg-white/10 rounded-xl overflow-hidden flex-shrink-0">
                  {selectedShow.poster && (
                    <img 
                      src={`${process.env.PUBLIC_URL}${selectedShow.poster}`}
                      alt={selectedShow.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white mb-2">{selectedShow.title}</h2>
                  <p className="text-white/60">
                    {loadingAssets ? 'Loading assets...' : `${assets.length} files available`}
                  </p>
                </div>
                {assets.length > 0 && (
                  <button
                    onClick={() => setSelectedAssets([...assets])}
                    className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#020120] rounded-xl font-semibold hover:bg-white transition-colors"
                  >
                    <Package className="w-5 h-5" />
                    Select All
                  </button>
                )}
              </div>

              {/* Search & Controls */}
              <div className="flex items-center justify-between mb-6 gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search files..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Check className="w-5 h-5" />
                    {assets.every(a => selectedAssets.find(s => s.name === a.name)) ? 'Deselect All' : 'Select All'}
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Assets */}
              {loadingAssets ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
                </div>
              ) : filteredAssets.length === 0 ? (
                <div className="text-center py-20">
                  <FolderOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No assets found for this show</p>
                  <p className="text-white/30 text-sm mt-2">Contact the team to request assets</p>
                </div>
              ) : (
                <div className="space-y-10">
                  {groupedAssets.map(({ cat, items }) => (
                    <div key={cat} data-testid={`media-portal-section-${cat.toLowerCase()}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <h3 className="text-lg font-bold text-[#D4AF37]">{CATEGORY_LABELS[cat] || cat}</h3>
                        <span className="text-white/40 text-sm">({items.length})</span>
                        <div className="flex-1 h-px bg-white/10" />
                      </div>
                      {viewMode === 'grid' ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {items.map((asset) => {
                    const isSelected = selectedAssets.find(a => a.name === asset.name);
                    const FileIcon = getFileIcon(asset.type);
                    const isImage = asset.type?.startsWith('image/');
                    
                    return (
                      <motion.div
                        key={asset.name}
                        className={`relative bg-white/5 border rounded-xl overflow-hidden cursor-pointer transition-all ${
                          isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 hover:border-white/30'
                        }`}
                        onClick={() => handleSelectAsset(asset)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {/* Checkbox */}
                        <div className={`absolute top-3 left-3 z-10 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                          isSelected ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-white/30 bg-black/50'
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-[#020120]" />}
                        </div>
                        
                        {/* Preview */}
                        <div className="aspect-video bg-white/5 flex items-center justify-center relative overflow-hidden">
                          {isImage ? (
                            <img 
                              src={asset.url} 
                              alt={asset.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <FileIcon className="w-12 h-12 text-white/30" />
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="p-4">
                          <p className="text-white font-medium text-sm truncate" title={asset.name}>{asset.displayName || asset.name}</p>
                          <p className="text-white/50 text-xs mt-1">{formatSize(asset.size)}</p>
                        </div>
                        
                        {/* Download Button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadSingle(asset); }}
                          className="absolute top-3 right-3 w-8 h-8 bg-black/50 hover:bg-[#D4AF37] rounded-lg flex items-center justify-center transition-colors"
                        >
                          <Download className="w-4 h-4 text-white" />
                        </button>
                      </motion.div>
                    );
                  })}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {items.map((asset) => {
                    const isSelected = selectedAssets.find(a => a.name === asset.name);
                    const FileIcon = getFileIcon(asset.type);
                    
                    return (
                      <motion.div
                        key={asset.name}
                        className={`flex items-center gap-4 p-4 bg-white/5 border rounded-xl cursor-pointer transition-all ${
                          isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 hover:border-white/30'
                        }`}
                        onClick={() => handleSelectAsset(asset)}
                      >
                        {/* Checkbox */}
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                          isSelected ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-white/30'
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-[#020120]" />}
                        </div>
                        
                        {/* Icon */}
                        <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileIcon className="w-6 h-6 text-white/50" />
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{asset.displayName || asset.name}</p>
                          <p className="text-white/50 text-sm">{formatSize(asset.size)}</p>
                        </div>
                        
                        {/* Download Button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadSingle(asset); }}
                          className="px-4 py-2 bg-white/10 hover:bg-[#D4AF37] text-white rounded-lg transition-colors flex items-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          <span className="hidden sm:inline">Download</span>
                        </button>
                      </motion.div>
                    );
                  })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
